<div class="horizon">
  <!-- ============================ Access ============================ -->
  <!-- The member's details on the left, every action on the right: one main button and the rest in the "⋯" menu. -->
  <div class="horizon-access" *ngIf="access">
    <div class="horizon-access__member">
      <span class="horizon-access__icon" aria-hidden="true"><i class="fa-regular fa-user"></i></span>
      <div class="horizon-access__text">
        <div class="horizon-access__line">
          <strong>Horizon access</strong>
          <span class="horizon-access__status" [ngClass]="'horizon-access__status--' + access.status">{{ accessStatusLabel }}</span>
          <span class="horizon-access__primary" *ngIf="access.is_primary_project">
            <i class="pi pi-star-fill" aria-hidden="true"></i>
            Main project
          </span>
        </div>
        <ng-container [ngSwitch]="access.status">
          <small *ngSwitchCase="'not_invited'">
            {{ access.email || 'No email on this lead' }} has not been invited yet.
            <span class="horizon-access__reason" *ngIf="inviteTooltip">{{ inviteTooltip }}</span>
          </small>
          <small *ngSwitchCase="'invited'">
            Invited {{ formatShortDate(access.invited_at) }}
            <ng-container *ngIf="access.invite_expires_at"> · link expires {{ formatShortDate(access.invite_expires_at) }}</ng-container>
          </small>
          <small *ngSwitchDefault>
            {{ access.email }}
            <ng-container *ngIf="lastActiveAt"> · Last active {{ formatActivityDate(lastActiveAt) }}</ng-container>
          </small>
        </ng-container>
      </div>
    </div>

    <div class="horizon-access__actions">
      <!-- The buyer's Horizon stage on this project; only once they have a Horizon account. -->
      <div class="horizon-access__stage" *ngIf="canSetHorizonStage">
        <span>Horizon stage</span>
        <ng-select
          [items]="horizonStages"
          bindLabel="label"
          bindValue="value"
          aria-label="Horizon stage"
          [searchable]="false"
          [clearable]="false"
          appendTo="body"
          [disabled]="isStageBusy"
          [(ngModel)]="horizonStage"
          (change)="changeHorizonStage($event?.value)"></ng-select>
      </div>
      <!-- The tab's date range sits on the access bar so it does not take a row of its own. -->
      <ng-container *ngIf="overview?.has_account" [ngTemplateOutlet]="rangePicker"></ng-container>
      <ng-container *ngIf="!pendingAccessAction; else confirmAccess">
        <button
          type="button"
          class="_primary-btn"
          *ngIf="access.status === 'not_invited'"
          [disabled]="!access.eligible || isAccessBusy"
          [attr.title]="inviteTooltip || null"
          (click)="invite()">
          Invite to Horizon
        </button>
        <button
          type="button"
          class="_primary-btn"
          *ngIf="access.status === 'invited'"
          [disabled]="!access.eligible || isAccessBusy"
          (click)="invite()">
          Resend invite
        </button>
        <button
          type="button"
          class="_primary-btn"
          *ngIf="access.status === 'suspended'"
          [disabled]="isAccessBusy"
          (click)="requestAccessAction('restore')">
          Restore access
        </button>
        <ng-container *ngIf="hasAccessMenu">
          <button
            type="button"
            class="icon_btn"
            aria-label="More Horizon actions"
            pTooltip="More actions"
            tooltipPosition="top"
            [disabled]="isAccessBusy"
            (click)="openAccessMenu($event, accessMenu)">
            <i class="pi pi-ellipsis-h"></i>
          </button>
          <p-menu #accessMenu [popup]="true" [model]="accessMenuItems" appendTo="body"></p-menu>
        </ng-container>
      </ng-container>
      <ng-template #confirmAccess>
        <span class="horizon-access__confirm">
          {{ pendingAccessAction === 'suspend' ? 'Block this client from signing in?' : 'Cancel the unused invite link?' }}
        </span>
        <button type="button" class="_danger-btn" [disabled]="isAccessBusy" (click)="runAccessAction(pendingAccessAction)">Confirm</button>
        <button type="button" class="_cancel-btn" (click)="pendingAccessAction = null">Back</button>
      </ng-template>
    </div>
  </div>

  <!-- Only when the access bar is missing (its request failed) does the range need a row of its own. -->
  <div class="horizon__toolbar" *ngIf="overview?.has_account && !access">
    <ng-container [ngTemplateOutlet]="rangePicker"></ng-container>
  </div>

  <ng-template #rangePicker>
    <div class="horizon__range">
      <ng-select
        [items]="dateRanges"
        bindLabel="label"
        bindValue="value"
        aria-label="Date range"
        [searchable]="false"
        [clearable]="false"
        appendTo="body"
        [(ngModel)]="selectedDays"
        (change)="load()"></ng-select>
    </div>
  </ng-template>

  <div class="horizon__notice" *ngIf="isLoading && !overview">
    <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
    Loading Horizon activity…
  </div>

  <div class="horizon__notice horizon__notice--error" *ngIf="hasLoadError">
    <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
    <span>Horizon activity could not be loaded.</span>
    <button type="button" class="_outline-btn" (click)="load()">Retry</button>
  </div>

  <div class="horizon__notice" *ngIf="overview && !overview.has_account">
    <i class="fa-regular fa-user" aria-hidden="true"></i>
    <span>This client does not have Horizon access yet.</span>
  </div>

  <div class="horizon__notice" *ngIf="overview?.has_account && !hasActivity">
    <i class="fa-regular fa-eye-slash" aria-hidden="true"></i>
    <span>No Horizon activity in this period.</span>
  </div>

  <ng-container *ngIf="overview?.has_account">
    <!-- ============================ Engagement summary ============================ -->
    <section class="horizon-card" *ngIf="hasActivity">
      <header class="horizon-card__head">
        <h3 class="horizon-card__title">Horizon Engagement Summary</h3>
      </header>
      <div class="horizon-summary">
        <!-- Every tile in the design is always drawn; one the backend does not track yet reads "—". -->
        <!-- Information Requests and Display Suite Bookings open the timeline below on those requests. -->
        <ng-container *ngFor="let card of summaryCards">
          <button
            type="button"
            class="horizon-summary__item horizon-summary__item--action"
            *ngIf="card.filter; else plainTile"
            [attr.aria-label]="'Show ' + card.label + ' in the activity timeline'"
            [attr.title]="metric(card.key) ? 'Show in the activity timeline' : 'Not tracked yet'"
            (click)="showActivityFor(card.filter)">
            <ng-container [ngTemplateOutlet]="tileBody" [ngTemplateOutletContext]="{ $implicit: card }"></ng-container>
          </button>
          <ng-template #plainTile>
            <div class="horizon-summary__item" [attr.title]="metric(card.key) ? null : 'Not tracked yet'">
              <ng-container [ngTemplateOutlet]="tileBody" [ngTemplateOutletContext]="{ $implicit: card }"></ng-container>
            </div>
          </ng-template>
        </ng-container>
        <ng-template #tileBody let-card>
          <span class="horizon-summary__icon"><i [class]="card.icon" aria-hidden="true"></i></span>
          <span class="horizon-summary__text">
            <span class="horizon-summary__top">
              <span class="horizon-summary__value">{{ tileValue(card) }}</span>
              <ng-container *ngIf="metric(card.key) as m">
                <span class="horizon-summary__change" *ngIf="hasComparison(m)" [class.horizon-summary__change--down]="m.change_pct < 0">
                  <i [class]="m.change_pct < 0 ? 'fa-solid fa-arrow-down' : 'fa-solid fa-arrow-up'" aria-hidden="true"></i>
                  {{ m.change_pct < 0 ? -m.change_pct : m.change_pct }}%
                </span>
              </ng-container>
            </span>
            <span class="horizon-summary__label" [title]="card.label">{{ card.label }}</span>
          </span>
        </ng-template>
      </div>
    </section>

    <div class="horizon__layout">
      <!-- ============================ Left column ============================ -->
      <div class="horizon__column">
        <app-horizon-matrix-heatmap
          [projectId]="projectId"
          [leadId]="leadId"
          [range]="dateRange"
          subtitle="Horizon activity from this buyer only."></app-horizon-matrix-heatmap>

        <section class="horizon-card" #activityTimeline>
          <header class="horizon-card__head">
            <div>
              <h3 class="horizon-card__title">Horizon Activity Timeline</h3>
              <p class="horizon-card__sub">Recent activity from this lead in Horizon</p>
            </div>
            <div class="horizon__range">
              <ng-select
                [items]="activityFilters"
                bindLabel="label"
                bindValue="value"
                [searchable]="false"
                [clearable]="false"
                appendTo="body"
                [(ngModel)]="selectedActivityFilter"
                (change)="loadActivity(true)"></ng-select>
            </div>
          </header>
          <ul class="horizon-timeline" *ngIf="activity.length; else noActivity" (scroll)="onTimelineScroll($event)">
            <li *ngFor="let item of activity; trackBy: trackByIndex" [class.horizon-timeline__item--request]="isRequest(item)">
              <span class="horizon-timeline__icon" [ngClass]="'horizon-timeline__icon--' + activityTone(item)">
                <i [class]="activityIcon(item)" aria-hidden="true"></i>
              </span>
              <span class="horizon-timeline__what">
                <strong>{{ item.title }}</strong>
                <!-- With no details to expand, a request's subtitle is what the buyer wrote, so it is shown in full, not cut to one line. -->
                <small *ngIf="item.subtitle" [class.horizon-timeline__full]="isRequest(item) && !hasDetail(item)">
                  {{ item.subtitle }}
                </small>
                <ng-container *ngIf="isRequest(item) && hasDetail(item)">
                  <button type="button" class="horizon-timeline__toggle" [attr.aria-expanded]="isExpanded(item)" (click)="toggleRequestDetail(item)">
                    {{ isExpanded(item) ? 'Hide details' : 'View details' }}
                    <i [class]="isExpanded(item) ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" aria-hidden="true"></i>
                  </button>
                  <dl class="horizon-timeline__detail" *ngIf="isExpanded(item)">
                    <ng-container *ngIf="item.detail?.topic">
                      <dt>Topic</dt>
                      <dd>{{ item.detail?.topic }}</dd>
                    </ng-container>
                    <ng-container *ngIf="item.detail?.lot_name">
                      <dt>Lot</dt>
                      <dd>{{ item.detail?.lot_name }}</dd>
                    </ng-container>
                    <ng-container *ngIf="item.detail?.phone">
                      <dt>Phone</dt>
                      <dd>
                        <a [href]="'tel:' + item.detail?.phone">{{ item.detail?.phone }}</a>
                      </dd>
                    </ng-container>
                    <ng-container *ngIf="item.detail?.preferred">
                      <dt>Preferred day / time</dt>
                      <dd>{{ item.detail?.preferred }}</dd>
                    </ng-container>
                    <ng-container *ngIf="item.detail?.message">
                      <dt>Message</dt>
                      <dd class="horizon-timeline__message">{{ item.detail?.message }}</dd>
                    </ng-container>
                  </dl>
                </ng-container>
              </span>
              <span class="horizon-timeline__when">{{ formatActivityDate(item.at) }}</span>
            </li>
          </ul>
          <ng-template #noActivity>
            <p class="horizon__empty">{{ isLoadingMoreActivity ? 'Loading…' : 'No activity for this filter.' }}</p>
          </ng-template>
          <p class="horizon__empty" *ngIf="activity.length && isLoadingMoreActivity">Loading…</p>
        </section>

        <section class="horizon-card">
          <header class="horizon-card__head">
            <div>
              <h3 class="horizon-card__title">Recent Property Activity</h3>
              <p class="horizon-card__sub">Properties this buyer has viewed most recently.</p>
            </div>
            <button type="button" class="horizon__link" *ngIf="showAllProperties || propertiesHasMore" (click)="toggleAllProperties()">
              {{ showAllProperties ? 'Show less' : 'View All Properties' }}
            </button>
          </header>
          <p class="horizon__empty" *ngIf="!properties.length">
            {{ isLoadingProperties ? 'Loading…' : 'This buyer has not viewed any properties in this period.' }}
          </p>
          <div class="horizon-table-wrap" *ngIf="properties.length">
            <table class="horizon-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Project</th>
                  <th>Views</th>
                  <th>Time Spent</th>
                  <th>Last Viewed</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of properties; trackBy: trackByIndex">
                  <td>
                    <div class="horizon-table__property">
                      <img *ngIf="row.image" [src]="row.image" alt="" width="32" height="32" loading="lazy" />
                      <span>
                        <strong class="horizon-table__lot">{{ propertyName(row) }}</strong>
                        <small *ngIf="row.lot_name && row.lot_name !== row.name">Lot {{ row.lot_name }}</small>
                      </span>
                    </div>
                  </td>
                  <td>{{ row.project_name }}</td>
                  <td>{{ row.views }}</td>
                  <td>{{ formatDuration(row.time_spent_seconds) }}</td>
                  <td>{{ formatActivityDate(row.last_viewed_at) }}</td>
                  <td>
                    <span class="horizon-status" [class.horizon-status--shortlisted]="row.shortlisted">
                      <i [class]="row.shortlisted ? 'fa-solid fa-heart' : 'fa-solid fa-eye'" aria-hidden="true"></i>
                      {{ row.shortlisted ? 'Shortlisted' : 'Viewed' }}
                    </span>
                  </td>
                  <td>
                    <button type="button" class="_outline-btn" (click)="openLot(row.project_id, row.unit_id)">View</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="horizon__more-row" *ngIf="showAllProperties && propertiesHasMore">
            <button type="button" class="_outline-btn" [disabled]="isLoadingProperties" (click)="loadMoreProperties()">
              {{ isLoadingProperties ? 'Loading…' : 'Load more' }}
            </button>
          </div>
        </section>
      </div>

      <!-- ============================ Right column ============================ -->
      <div class="horizon__column">
        <app-horizon-buyer-insights
          [insights]="insights"
          [isLoading]="isInsightsLoading"
          [hasLoadError]="hasInsightsError"></app-horizon-buyer-insights>

        <app-horizon-buyer-preferences [leadId]="leadId" [projectId]="projectId" [range]="dateRange"></app-horizon-buyer-preferences>

        <section class="horizon-card">
          <header class="horizon-card__head">
            <div>
              <h3 class="horizon-card__title">Most Viewed Properties</h3>
              <p class="horizon-card__sub">The five properties this buyer has viewed most, by their views.</p>
            </div>
            <button type="button" class="horizon__link" *ngIf="projectId" (click)="viewProjectReport()">View Project Report</button>
          </header>
          <p class="horizon__empty" *ngIf="!buyerMostViewed.length">This buyer has not viewed any properties in this period.</p>
          <ol class="horizon-ranking" *ngIf="buyerMostViewed.length">
            <li *ngFor="let row of buyerMostViewed; let i = index; trackBy: trackByIndex">
              <span class="horizon-ranking__rank">{{ i + 1 }}</span>
              <span class="horizon-ranking__name" [title]="propertyName(row)">{{ propertyName(row) }}</span>
              <span class="horizon-ranking__bar"><i [style.width.%]="(row.views / mostViewedMax) * 100"></i></span>
              <span class="horizon-ranking__count">{{ row.views }}</span>
            </li>
          </ol>
        </section>

        <app-horizon-shared-with-buyer [leadId]="leadId" [projectId]="projectId" [canShare]="canShareWithBuyer"></app-horizon-shared-with-buyer>
      </div>
    </div>
  </ng-container>
</div>
